import { FocusEvent, useEffect, useState } from 'react';
import { FormikErrors } from 'formik';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { Logger } from '@utils/logger';
import { useSelector } from 'react-redux';
import { getLanguageContent } from '@redux/selectors/language-selectors';
import { SelectOption } from '@iddataweb/idw-common-ui/types';
import { AXNUISelectInputWithErrorMessage } from '@iddataweb/idw-common-ui/containers';
import { NATIONALITY_SELECT_OPTIONS } from '@constants/nationality';

type NationalityInputProps = {
	title: string;
	attribute: AttributeFormState;
	fieldName: string;
	fieldTouched: boolean;
	fieldError: string;
	fieldIsInvalid: boolean;
	fieldShouldBeHighlighted: boolean;
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	onBlur: (event: FocusEvent<HTMLInputElement>) => void;
	onSetSubmitValue: (data: any) => void;
	isSubmitting: boolean;
};

export const NationalityInput = (props: NationalityInputProps) => {
	const {
		title,
		attribute,
		fieldName,
		fieldTouched,
		fieldError,
		fieldIsInvalid,
		fieldShouldBeHighlighted,
		setFieldValue,
		setFieldTouched,
		onBlur,
		onSetSubmitValue,
		isSubmitting
	} = props;

	const content = useSelector(getLanguageContent);

	const getInitialSelectedOption = () => {
		if (attribute.inputData.nationality) {
			const option = NATIONALITY_SELECT_OPTIONS.find(co => co.value === attribute.inputData.nationality);
			if (option) {
				return option;
			}
		}
		return null;
	};

	const [selectedOption, setSelectedOption] = useState(getInitialSelectedOption());

	useEffect(() => {
		if (!attribute.inputData.nationality) {
			setSelectedOption(null);
		} else {
			const option = NATIONALITY_SELECT_OPTIONS.find(nationality => nationality.value === attribute.inputData.nationality);
			if (option) {
				setSelectedOption(option);
			}
		}
	}, [attribute.inputData.nationality]);

	const handleChange = async (newValue: SelectOption) => {
		Logger.info("[NationalityAttribute] Selected Nationality: ", newValue);
		if (newValue) {
			const { value } = newValue;
			onSetSubmitValue({ inputData: { nationality: value } });
			await setFieldValue(fieldName, value);
			setFieldTouched(fieldName, true, true);
		} else {
			onSetSubmitValue({ inputData: { nationality: "" } });
			await setFieldValue(fieldName, "");
			setFieldTouched(fieldName, true, true);
		}
	};

	return (
		<AXNUISelectInputWithErrorMessage
			controlId={`attr${attribute.id}-nationality-input`}
			automationId={`attr${attribute.id}-nationality-input`}
			ariaLabel={content.validation.nationality.required}
			isClearable={attribute.isOptional}
			isSearchable={true}
			title={title}
			placeholder={attribute.placeholder
				? attribute.placeholder
				: content.validation.nationality.required}
			selectedOption={selectedOption}
			options={NATIONALITY_SELECT_OPTIONS}
			fieldName={fieldName}
			fieldTouched={fieldTouched}
			fieldError={fieldError}
			fieldIsInvalid={fieldIsInvalid}
			fieldShouldBeHighlighted={fieldShouldBeHighlighted}
			onBlur={onBlur}
			setFieldTouched={setFieldTouched}
			isDisabled={attribute.isDisabled}
			isPlaceholder={attribute.isPlaceholder}
			isOptional={attribute.optional}
			onChange={handleChange}
			isSubmitting={isSubmitting}
		/>
	);
};